import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  __experimentalFormField as FormField,
  __experimentalFormFieldProps as FormFieldProps,
  __experimentalAutocompleteInput,
  withChangeableValue,
  withErrorTooltip,
  withTransformer,
} from '@cp/ds/src/components/form';
import { ListItemText } from '@cp/ds/src/components/menuList';
import CircularProgress from '@mui/material/CircularProgress';
import { useFormContext } from 'react-hook-form';
import { IAddressOption, ICityOption, cityModelToOption, useCityAutocomplete } from '../model';
import { coreLocationApi } from '../api';

const AutocompleteInput = withErrorTooltip(withChangeableValue(__experimentalAutocompleteInput));

export interface CityFieldProps extends Partial<FormFieldProps> {
  cityId?: number;
  onOptionsUpdate?: (suggestions: IAddressOption[]) => void;
}
export const CityField: React.FC<CityFieldProps> = ({ name = 'cityId', onOptionsUpdate, ...props }) => {
  const autocomplete = useCityAutocomplete();
  const optionsCache = useRef<ICityOption[]>([]);
  const [inProgress, setInProgress] = useState(false);
  const [getCityById] = coreLocationApi.endpoints.getCityById.useLazyQuery();
  const getOptions = useCallback(
    async (query: string) => {
      setInProgress(true);
      return autocomplete(query)
        .then((o) => {
          optionsCache.current = o;
          onOptionsUpdate?.(o);
          return o;
        })
        .finally(() => setInProgress(false));
    },
    [autocomplete, onOptionsUpdate],
  );
  const { getValues } = useFormContext();

  // Load city translation for current value
  useEffect(() => {
    const fieldValue = getValues(name);
    const ac = new AbortController();
    const loadCityTranslation = async () => {
      if (optionsCache.current.length || !fieldValue) {
        return;
      }
      setInProgress(true);
      const { data } = await getCityById({ id: fieldValue }, true);
      if (ac.signal.aborted) {
        setInProgress(false);
        return;
      } else if (data) {
        optionsCache.current = [cityModelToOption(data)];
      }
      setInProgress(false);
    };
    loadCityTranslation();
    return () => {
      ac.abort();
    };
  }, [name, getValues, getCityById]);

  const renderOption = useCallback(
    (option: ICityOption) => (
      <ListItemText
        primary={option.label}
        secondary={option.region !== option.label ? option.region : null}
        sx={{ fontSize: '14px', whiteSpace: 'normal' }}
      />
    ),
    [],
  );

  const Control = useMemo(
    () =>
      withTransformer(AutocompleteInput, {
        fromControl: (v) => (Number.isNaN(Number(v)) ? null : Number(v)),
        toControl: (cityId) => {
          const option = optionsCache.current.find((o) => o.id === Number(cityId));
          return option?.label;
        },
      }),
    [],
  );

  return (
    <FormField
      name={name}
      {...props}
      render={({ size: _size, ...controlProps }) => (
        <Control
          {...controlProps}
          endAdornment={inProgress ? <CircularProgress color="inherit" size={20} /> : null}
          getOptions={getOptions}
          renderOption={renderOption}
        />
      )}
    />
  );
};
